'use client';

import { useEffect } from 'react';

// Lightweight client-side error reporter. Listens for uncaught errors and
// unhandled promise rejections, POSTs a compact payload to /api/errors, and is
// careful to never throw from inside itself (a reporter that crashes is worse
// than no reporter). Also exports `reportError` so a React error boundary can
// hand-feed it.

const SESSION_CAP = 10; // max reports per page session
const DEDUPE_MS = 30 * 1000; // suppress identical message+stack within window

// Module-level state survives across components for the page's lifetime.
let _sent = 0;
const _recent = new Map(); // hash -> last-sent timestamp

function hash(message, stack) {
  return (message || '') + ' ' + (stack || '');
}

function shouldSend(message, stack) {
  if (_sent >= SESSION_CAP) return false;
  const key = hash(message, stack);
  const now = Date.now();
  const last = _recent.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  _recent.set(key, now);
  // Keep the dedupe map from growing unbounded.
  if (_recent.size > 100) {
    for (const [k, t] of _recent) {
      if (now - t >= DEDUPE_MS) _recent.delete(k);
    }
  }
  return true;
}

// Een MISLUKTE SCHRIJFACTIE is geen crash: de app vangt de 4xx/5xx netjes af, dus
// noch de boundary noch de rejection-listener ziet 'm en er bleef nergens een spoor
// achter. Precies zo verdween op 21 juli een aangemaakte kandidaat "in het niets"
// (Sydney Wells): de gebruiker zag een toast, wij zagen niets. Deze helper laat een
// afgevangen schrijffout in dezelfde ledger landen als een crash - zelfde sessie-cap
// en dedupe - met kind 'mutation', zodat Beheer > Monitor ze kan tonen.
//
// Aanroepen op de plek waar de fout wordt afgevangen:
//   reportMutationFailure({ action: 'create-candidate', status: 400, reason: 'missing_market', message: '...' })
export function reportMutationFailure(info) {
  const i = info || {};
  const action = i.action || 'unknown';
  const reason = i.reason ? ` [${i.reason}]` : '';
  const status = i.status ? ` ${i.status}` : '';
  reportError({
    message: `[mutation${status}] ${action}${reason}: ${i.message || 'onbekende fout'}`,
    // Geen JS-stack (er is geen throw); de context is wat de actie was en waarom
    // die faalde - dat is precies wat een reconstructie later nodig heeft.
    stack: JSON.stringify({ action, status: i.status || null, reason: i.reason || null, detail: i.detail || null }, null, 2),
    kind: 'mutation',
  });
}

// Exported so an error boundary can call it directly: reportError({message, stack, ...}).
export function reportError(info) {
  try {
    const i = info || {};
    const message = i.message || '';
    const stack = i.stack || '';
    if (!shouldSend(message, stack)) return;
    _sent++;

    const payload = JSON.stringify({
      message: String(message).slice(0, 2000),
      stack: String(stack).slice(0, 8000),
      url: i.url || (typeof location !== 'undefined' ? location.href : ''),
      kind: i.kind || 'boundary',
      appVersion:
        (typeof window !== 'undefined' && window.__APP_VERSION__) || '',
    });

    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon('/api/errors', blob);
      if (ok) return;
    }
    // Fallback — keepalive so the request survives an unloading page.
    if (typeof fetch === 'function') {
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Swallow — the reporter must never throw.
  }
}

export default function ErrorReporter() {
  useEffect(() => {
    function onError(e) {
      try {
        reportError({
          kind: 'error',
          message: e?.message,
          stack: e?.error?.stack,
          url: typeof location !== 'undefined' ? location.href : '',
        });
      } catch {}
    }
    function onRejection(e) {
      try {
        const reason = e?.reason;
        reportError({
          kind: 'unhandledrejection',
          message: String((reason && reason.message) || reason || ''),
          stack: reason && reason.stack,
          url: typeof location !== 'undefined' ? location.href : '',
        });
      } catch {}
    }

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
